import { urls as profileUrls } from 'src/urls/profile';
import { authRequest } from 'src/utils/fetch';

import { PreferenceKey } from './preferences';

function rejectionError(op: string, detail: string): Error {
  return new Error(`scoped-preferences ${op} failed: ${detail}`);
}

async function doGet(url: string): Promise<any> {
  const response = await fetch(authRequest(url));
  if (response.ok) {
    return response.json();
  } else {
    // If the response status is 404, it means the preference has not been set yet.
    // It makes more sense to resolve the promise with a value`undefined` instead of rejecting it.
    if (response.status === 404) {
      return Promise.resolve(undefined);
    }
  }
  return Promise.reject(
    rejectionError('GET', `${response.status} ${response.statusText}`)
  );
}

async function doDelete(url: string): Promise<void> {
  const response = await fetch(authRequest(url, { method: 'DELETE' }));
  return response.ok
    ? Promise.resolve()
    : Promise.reject(
        rejectionError('DELETE', `${response.status} ${response.statusText}`)
      );
}

async function doSet(url: string, value: any): Promise<any> {
  const data = new FormData();
  data.append('value', value);

  const response = await fetch(
    authRequest(url, {
      method: 'PUT',
      body: data,
    })
  );
  return response.ok
    ? response.json()
    : Promise.reject(
        rejectionError('SET', `${response.status} ${response.statusText}`)
      );
}

export const userWorkspacePreferences = {
  get: (
    userUuid: string,
    workspaceUuid: string,
    key: PreferenceKey
  ): Promise<any> => {
    if (!userUuid || !workspaceUuid) {
      return Promise.reject(
        rejectionError('GET', 'missing userUuid or workspaceUuid')
      );
    }

    const url = profileUrls.api.internal.userWorkspacePreference(
      userUuid,
      workspaceUuid,
      encodeURIComponent(key.toString())
    );
    return doGet(url);
  },

  set: (
    userUuid: string,
    workspaceUuid: string,
    key: PreferenceKey,
    value: any
  ): Promise<any> => {
    if (!userUuid || !workspaceUuid) {
      return Promise.reject(
        rejectionError('SET', 'missing userUuid or workspaceUuid')
      );
    }

    const url = profileUrls.api.internal.userWorkspacePreference(
      userUuid,
      workspaceUuid,
      encodeURIComponent(key.toString())
    );

    return doSet(url, value);
  },

  delete: (
    userUuid: string,
    workspaceUuid: string,
    key: PreferenceKey
  ): Promise<void> => {
    if (!userUuid || !workspaceUuid) {
      return Promise.reject(
        rejectionError('DELETE', 'missing userUuid or workspaceUuid')
      );
    }

    const url = profileUrls.api.internal.userWorkspacePreference(
      userUuid,
      workspaceUuid,
      encodeURIComponent(key.toString())
    );

    return doDelete(url);
  },
};

export const userRepoPreferences = {
  get: (
    userUuid: string,
    repoUuid: string,
    key: PreferenceKey
  ): Promise<any> => {
    if (!userUuid || !repoUuid) {
      return Promise.reject(
        rejectionError('GET', 'missing userUuid or repoUuid')
      );
    }

    const url = profileUrls.api.internal.userRepositoryPreference(
      userUuid,
      repoUuid,
      encodeURIComponent(key.toString())
    );

    return doGet(url);
  },

  set: (
    userUuid: string,
    repoUuid: string,
    key: PreferenceKey,
    value: any
  ): Promise<any> => {
    if (!userUuid || !repoUuid) {
      return Promise.reject(
        rejectionError('SET', 'missing userUuid or repoUuid')
      );
    }

    const url = profileUrls.api.internal.userRepositoryPreference(
      userUuid,
      repoUuid,
      encodeURIComponent(key.toString())
    );

    return doSet(url, value);
  },

  delete: (
    userUuid: string,
    repoUuid: string,
    key: PreferenceKey
  ): Promise<void> => {
    if (!userUuid || !repoUuid) {
      return Promise.reject(
        rejectionError('DELETE', 'missing userUuid or repoUuid')
      );
    }

    const url = profileUrls.api.internal.userRepositoryPreference(
      userUuid,
      repoUuid,
      encodeURIComponent(key.toString())
    );

    return doDelete(url);
  },
};
